import React, { useState, useRef } from 'react';
import block from 'propmods';
import { useAudio } from 'react-use';
import {
    ThereminvoxSoundMeta,
    ThereminvoxTestingModeProps,
    thereminvoxSoundsDragHandleId,
} from '../../model/thereminvox';
import MinusIcon from '../../icons/minus.svg';
import ReloadIcon from '../../icons/reload.svg';
import HamburgerIcon from '../../icons/hamburger-menu.svg';
import ErrorSmileIcon from '../../icons/smile.svg';
import { Bold } from '../Typo/Typo';
import { useTouchHandlers } from '../../hooks/useTouchHandlers';
import { AudioProgress } from '../AudioProgress/AudioProgress';
import { PlayIcon } from '../../icons/play';
import { PauseIcon } from '../../icons/pause';

import './ThereminvoxAudio.scss';

const b = block('ThereminvoxAudio');

interface ThereminvoxAudioProgressProps {
    disabled?: boolean;
    audioState: { duration: number; time: number };
    onManualSeek: (value: number) => void;
    onSeekEnd: () => void;
}

const ThereminvoxAudioProgress: React.FC<ThereminvoxAudioProgressProps> = ({
    disabled,
    audioState,
    onManualSeek,
    onSeekEnd,
}) => {
    const { duration, time } = audioState;

    return (
        <div {...b('progress')}>
            <AudioProgress
                disabled={disabled}
                duration={duration}
                value={time}
                trackColorActive="#6839cf"
                onChange={onManualSeek}
                onFinalChange={onSeekEnd}
            />
        </div>
    );
};

interface Props extends ThereminvoxTestingModeProps {
    soundMeta: ThereminvoxSoundMeta;
    disabled?: boolean;
    isTestingMode?: boolean;
    onRemove?: () => void;
    position: number;
}

export const ThereminvoxAudio: React.FC<Props> = ({
    soundMeta,
    disabled: outerDisabled,
    isTestingMode,
    onRemove,
    position,
    repeatSoundInside,
    noOverlaySamples,
    stopOnCeil,
    stopAllSounds,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isMouseOver, setIsMouseOver] = useState(false);
    const [isSeeking, setIsSeeking] = useState(false);
    const [audio, audioState, audioControls] = useAudio({
        hidden: true,
        src: `${__appConfig.app.staticResourcesUrl}/dialogs/${soundMeta.originalPath}`,
        preload: 'auto',
        loop: isMouseOver && repeatSoundInside,
    });

    const onMouseEnter = () => {
        setIsMouseOver(true);

        if (isTestingMode) {
            if (noOverlaySamples) {
                stopAllSounds();
            }

            seek(0);
            void play();
        }
    };

    const onMouseLeave = () => {
        setIsMouseOver(false);
    };

    const { isTouchInside } = useTouchHandlers(
        {
            onTouchStart: onMouseEnter,
            onTouchEnd: () => {
                onMouseLeave();
                if (stopOnCeil) {
                    stopAllSounds();
                }
            },
            onTouchEnter: onMouseEnter,
            onTouchLeave: onMouseLeave,
            elementRef: containerRef,
            active: isTestingMode,
        },
        [isTestingMode, repeatSoundInside, noOverlaySamples, stopOnCeil],
    );

    const { paused, duration, time } = audioState;
    const { play, pause, seek } = audioControls;

    const disabled = outerDisabled || !duration;
    const showProgress = !isTestingMode && ((time !== 0 && duration !== time) || isSeeking);

    const currentAudioControl = paused ? play : pause;
    const CurrentAudioControlIcon = paused ? PlayIcon : PauseIcon;

    return (
        <div
            {...b({ disabled, testing: isTestingMode, touched: isTouchInside })}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            ref={containerRef}
        >
            <div {...b('controls')}>
                <div {...b('control-container', { disabled })}>
                    <div {...b('control', { hidden: isTestingMode })} onClick={currentAudioControl}>
                        <CurrentAudioControlIcon size="l" color="#6839CF" />
                    </div>
                    <div {...b('wave', { disabled: !isTestingMode, position })} />
                </div>
                <div {...b('body')}>
                    <div {...b('name', { disabled, lifted: showProgress })}>{soundMeta.originalName}</div>
                    {showProgress && (
                        <ThereminvoxAudioProgress
                            disabled={disabled}
                            audioState={audioState}
                            onManualSeek={(newCurrentTime) => {
                                pause();
                                setIsSeeking(true);
                                seek(newCurrentTime);
                            }}
                            onSeekEnd={() => {
                                void play();
                                setIsSeeking(false);
                            }}
                        />
                    )}
                </div>
                <div {...b('remove-button', { hidden: isTestingMode, disabled })} onClick={onRemove}>
                    <MinusIcon />
                </div>
                <div {...b('drag-handle', { hidden: isTestingMode, disabled })} id={thereminvoxSoundsDragHandleId}>
                    <HamburgerIcon />
                </div>
            </div>
            {audio}
        </div>
    );
};

interface UploadingProps {
    soundName: string;
    onErrorReload?: () => void;
    onRemove?: () => void;
}

export const ThereminvoxAudioUploading: React.FC<UploadingProps> = ({ soundName, onErrorReload, onRemove }) => {
    const isError = Boolean(onErrorReload);

    const CurrentAudioControlIcon = isError ? ErrorSmileIcon : PlayIcon;
    const CurrentDragHandleIcon = isError ? ReloadIcon : HamburgerIcon;

    return (
        <div {...b({ disabled: !isError })}>
            <div {...b('controls')}>
                <div {...b('control-container')}>
                    <div {...b('control', { disabled: !isError, error: isError })}>
                        <CurrentAudioControlIcon />
                    </div>
                </div>
                <div {...b('body')}>
                    {isError ? (
                        <div {...b('error')}>
                            Что-то пошло не так...
                            <br />
                            <Bold>{soundName}</Bold>
                        </div>
                    ) : (
                        <div {...b('name', { disabled: !isError, lifted: true })}>{soundName}</div>
                    )}
                    {!isError && <div {...b('progress-uploading')} />}
                </div>
                <div
                    {...b('remove-button', { disabled: !isError, error: isError })}
                    onClick={isError ? onRemove : undefined}
                >
                    <MinusIcon />
                </div>
                <div {...b('drag-handle', { disabled: !isError, error: isError })} onClick={onErrorReload}>
                    <CurrentDragHandleIcon />
                </div>
            </div>
        </div>
    );
};
